/**
 * CE Data Export Tab Component
 * Displays all CE data in MUI DataGrid for CSV export
 */

import { useState, useMemo, useCallback } from 'react'
import { sort } from 'ramda'
import {
  Autocomplete,
  Box,
  Skeleton,
  Alert,
  Typography,
  Chip,
  Button,
  TextField,
} from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  GridFilterInputValueProps,
} from '@mui/x-data-grid-premium'
import {
  FileDownload as ExportIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import axios from 'src/utilities/http-client'
import { downloadFile } from 'src/v3/utilities/filesystem'
import { getCertificateUrl } from 'src/v3/manage/utils/certificate-download'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { useAppSelector } from 'src/store/hooks'
import { selectGroupsByLinkedService } from 'src/v3/manage/manage.slice'
import { DashboardQueryParams, RollupDataItem } from '../../types'
import { useRollupData } from '../../hooks/use-rollup-data'

interface CEDataExportTabProps {
  queryParams: DashboardQueryParams
}

/**
 * Transform rollup data for DataGrid (add id field required by MUI)
 */
function transformRollupData(
  data: RollupDataItem[],
): (RollupDataItem & { id: string })[] {
  return data.map((item, index) => ({
    ...item,
    id: `${item.certificateID}-${index}`,
  }))
}

function GroupFilterInput({
  item,
  applyValue,
  groupOptions,
}: GridFilterInputValueProps & { groupOptions: string[] }) {
  return (
    <Autocomplete<string, true>
      multiple
      options={groupOptions}
      value={(item.value as string[]) ?? []}
      onChange={(_, newValue) => applyValue({ ...item, value: newValue })}
      renderInput={(params) => (
        <TextField {...params} label="Value" size="small" />
      )}
      sx={{ minWidth: 200 }}
    />
  )
}

export default function CEDataExportTab({ queryParams }: CEDataExportTabProps) {
  let { data: rollupData, isLoading, error } = useRollupData(queryParams)
  let [downloadingCerts, setDownloadingCerts] = useState<Set<string>>(new Set())

  let groups = useAppSelector(
    selectGroupsByLinkedService(queryParams.linkedServiceID),
  )

  let groupOptions = useMemo(
    () =>
      sort(
        (a, b) => a.localeCompare(b),
        groups.map((g) => g.name),
      ),
    [groups],
  )

  let ExportTabToolbar = useCallback(
    () => <DataGridToolbar fileName="ce-data-export" />,
    [],
  )

  // Transform data for DataGrid
  let transformedData = useMemo(() => {
    if (!rollupData) return []
    return transformRollupData(rollupData)
  }, [rollupData])

  // Handle certificate download
  let handleCertificateDownload = async (
    item: RollupDataItem & { id: string },
  ) => {
    if (!item.certificateID || !item.hasCertificate) return

    let certId = item.certificateID
    setDownloadingCerts((prev) => new Set(prev).add(certId))

    try {
      let certificateType = item.type === 'live' ? 'live' : 'recorded'
      let response = await getCertificateUrl(certId, certificateType)
      let fileResponse = await axios.get(response, { responseType: 'blob' })
      let filename = `${item.userName.replace(/\s+/g, '_')}_${item.courseActivity.replace(/\s+/g, '_')}_${certId}.pdf`

      await downloadFile(fileResponse.data, filename)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error downloading certificate:', err)
    } finally {
      setDownloadingCerts((prev) => {
        let newSet = new Set(prev)
        newSet.delete(certId)
        return newSet
      })
    }
  }

  // Define DataGrid columns
  let columns: GridColDef[] = [
    { field: 'userName', headerName: 'Name', width: 200, sortable: true },
    { field: 'email', headerName: 'Email', width: 250, sortable: true },
    {
      field: 'courseActivity',
      headerName: 'Course/Activity',
      width: 300,
      sortable: true,
    },
    {
      field: 'completionDate',
      headerName: 'Completion Date',
      width: 150,
      sortable: true,
      type: 'date',
      valueGetter: (value: string) =>
        value ? dayjs(value).toDate() : undefined,
      valueFormatter: (value: Date | undefined) =>
        value ? dayjs(value).format('MMM DD, YYYY') : '',
    },
    {
      field: 'ceHours',
      headerName: 'CE Hours',
      width: 100,
      type: 'number',
      sortable: true,
    },
    {
      field: 'accreditationStatus',
      headerName: 'Accreditation Status',
      width: 150,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          variant="outlined"
          sx={{
            backgroundColor: 'transparent',
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        />
      ),
    },
    { field: 'category', headerName: 'Category', width: 200, sortable: true },
    {
      field: 'licenseTypes',
      headerName: 'License Types',
      width: 150,
      sortable: true,
      renderCell: (params) => <span>{params.value || 'Unknown'}</span>,
    },
    {
      field: 'groups',
      headerName: 'Groups',
      width: 220,
      sortable: false,
      filterOperators: [
        {
          label: 'contains',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            let selected = filterItem.value as string[] | undefined
            if (!selected || selected.length === 0) return null
            return (value: string[]) => selected.some((g) => value?.includes(g))
          },
          InputComponent: (props: GridFilterInputValueProps) => (
            <GroupFilterInput {...props} groupOptions={groupOptions} />
          ),
        },
      ],
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            flexWrap: 'wrap',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {(params.value as string[])?.map((g) => (
            <Chip key={g} label={g} size="small" variant="outlined" />
          ))}
        </Box>
      ),
      valueFormatter: (value: string[]) => value?.join(', ') ?? '',
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={
            params.value === 'recorded'
              ? 'primary'
              : params.value === 'live'
                ? 'secondary'
                : params.value === 'external'
                  ? 'info'
                  : 'default'
          }
          variant="outlined"
        />
      ),
    },
    {
      field: 'courseCode',
      headerName: 'Course Code',
      width: 120,
      sortable: true,
    },
    {
      field: 'certificateID',
      headerName: 'Certificate',
      width: 185,
      sortable: false,
      renderCell: (params) => {
        let item = params.row as RollupDataItem & { id: string }
        let isDownloading = downloadingCerts.has(item.certificateID)

        if (!item.hasCertificate || !item.certificateID) {
          return <span style={{ color: 'gray' }}>No Certificate</span>
        }

        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={(e) => {
              e.stopPropagation()
              handleCertificateDownload(item)
            }}
            disabled={isDownloading}
            sx={{ minWidth: 140 }}
          >
            {isDownloading ? 'Downloading...' : 'Download Certificate'}
          </Button>
        )
      },
      valueGetter: () => 'Download Certificate',
    },
    {
      field: 'wasAssigned',
      headerName: 'Was Assigned',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'secondary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'assignmentName',
      headerName: 'Assignment Name',
      width: 200,
      sortable: true,
    },
    {
      field: 'assignmentDueDate',
      headerName: 'Assignment Due Date',
      width: 180,
      sortable: true,
      type: 'date',
      valueGetter: (value: string | null | undefined) =>
        value ? dayjs(value).toDate() : undefined,
      valueFormatter: (value: Date | undefined) =>
        value ? dayjs(value).format('MMM DD, YYYY') : '-',
    },
    {
      field: 'eventType',
      headerName: 'Event Type',
      width: 120,
      sortable: true,
    },
    {
      field: 'hasCertificate',
      headerName: 'Has Certificate',
      width: 140,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
  ]

  if (error) {
    let errorMessage = (() => {
      if (!(error instanceof Error)) {
        return 'Unable to load export data from the server'
      }

      if (error.message.includes('404')) {
        return 'Export endpoint not found. Please check your configuration.'
      }
      if (error.message.includes('401') || error.message.includes('403')) {
        return 'You do not have permission to access export data.'
      }
      if (error.message.includes('500')) {
        return 'Server error occurred while processing export request.'
      }
      if (error.message.includes('timeout')) {
        return 'Request timed out. The server may be experiencing high load.'
      }
      if (error.message.includes('Network')) {
        return 'Network error. Please check your internet connection.'
      }
      return `Failed to load export data: ${error.message}`
    })()

    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          Export Data Error
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Try refreshing the page or adjusting your date range. If the problem
          persists, contact support.
        </Typography>
      </Alert>
    )
  }

  if (isLoading) {
    return <Skeleton variant="rectangular" height={600} />
  }

  if (!transformedData || transformedData.length === 0) {
    return (
      <Box
        sx={{
          height: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'text.secondary',
        }}
      >
        <ExportIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          No Data to Export
        </Typography>
        <Typography variant="body2">
          No CE activities found for the selected date range. Try adjusting your
          date range or linked service selection.
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {transformedData.length} activities •{' '}
        {new Set(transformedData.map((item) => item.email)).size} users
      </Typography>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGridPremium
          rows={transformedData}
          columns={columns}
          slots={{ toolbar: ExportTabToolbar }}
          showToolbar
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: {
              sortModel: [{ field: 'completionDate', sort: 'desc' }],
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-toolbarContainer': {
              borderBottom: 1,
              borderColor: 'divider',
              p: 2,
            },
          }}
        />
      </Box>
    </>
  )
}
